// The account screen: sign in, register, and everything you can do to an account you already have.
//
// It draws one of four things — signed out, signed in, the recovery request, and the new password
// a link from the mail asks for — and never decides any of them: what the server answers is what
// is shown, including the refusals, which arrive already written in words.
//
// Playing without an account stays one click away on purpose. A friend who opens the link for one
// evening should not have to register to sit down; what they give up is the history, and the
// screen says so rather than insisting.

import { el, clear } from './dom.js';

export const AccountMode = Object.freeze({
  SIGNED_OUT: 'signed-out',
  SIGNED_IN: 'signed-in',
  FORGOT: 'forgot',
  RESET: 'reset',
});

export class AccountView {
  #root;
  #content;
  #status;
  #handlers;
  #mode = AccountMode.SIGNED_OUT;
  #account = null;
  #emailEnabled = false;
  #resetToken = '';
  #busy = false;

  // handlers: { onBack, onLogin, onRegister, onLogout, onChangePassword, onSetEmail,
  // onRequestReset, onResetPassword, onClaimHistory, onDeleteAccount, onStats }
  constructor(handlers) {
    this.#handlers = handlers;
    this.#status = el('p', { className: 'status' });
    this.#content = el('div', {});
    this.#root = el('div', { className: 'screen' }, [
      el('h1', { className: 'title', text: 'Il tuo account' }),
      this.#status,
      this.#content,
      el('button', {
        className: 'button button-soft',
        type: 'button',
        text: 'Torna indietro',
        onclick: () => this.#handlers.onBack(),
      }),
    ]);
  }

  get element() {
    return this.#root;
  }

  // account is null when nobody is signed in. emailEnabled comes from the catalogue: a server
  // that cannot send mail must not offer a link it will never deliver
  update({ account, emailEnabled, mode, resetToken } = {}) {
    if (account !== undefined) this.#account = account;
    if (emailEnabled !== undefined) this.#emailEnabled = emailEnabled;
    if (resetToken !== undefined) this.#resetToken = resetToken;
    this.#mode = mode ?? (this.#account ? AccountMode.SIGNED_IN : AccountMode.SIGNED_OUT);
    this.#render();
  }

  say(text, isError = false) {
    this.#status.textContent = text;
    this.#status.classList.toggle('status-error', Boolean(isError));
  }

  #render() {
    clear(this.#content);
    switch (this.#mode) {
      case AccountMode.SIGNED_IN:
        this.#content.append(...this.#signedInPanels());
        break;
      case AccountMode.FORGOT:
        this.#content.append(this.#forgotPanel());
        break;
      case AccountMode.RESET:
        this.#content.append(this.#resetPanel());
        break;
      default:
        this.#content.append(...this.#signedOutPanels());
    }
  }

  /** Signed out **/

  #signedOutPanels() {
    const name = field('accountName', 'Nome', { autocomplete: 'username' });
    const password = field('accountPassword', 'Password', {
      type: 'password',
      autocomplete: 'current-password',
    });
    const signIn = panel('Accedi', [
      name.label,
      name.input,
      password.label,
      password.input,
      button('Accedi', 'gold', () =>
        this.#run(() => this.#handlers.onLogin({
          userName: name.input.value.trim(),
          password: password.input.value,
        })),
      ),
      this.#emailEnabled
        ? link('Password dimenticata?', () => this.update({ mode: AccountMode.FORGOT }))
        : null,
    ]);

    const newName = field('newAccountName', 'Nome', { autocomplete: 'username' });
    const newPassword = field('newAccountPassword', 'Password (almeno 8 caratteri)', {
      type: 'password',
      autocomplete: 'new-password',
    });
    const email = field('newAccountEmail', 'Email (facoltativa)', {
      type: 'email',
      autocomplete: 'email',
    });
    const register = panel('Non hai un account', [
      el('p', {
        className: 'stats-hint',
        text: "Il nome è unico: nessun altro potrà presentarsi con il tuo al tavolo. Le partite giocate finora da questo dispositivo diventano tue.",
      }),
      newName.label,
      newName.input,
      newPassword.label,
      newPassword.input,
      email.label,
      email.input,
      el('p', {
        className: 'stats-hint',
        text: this.#emailEnabled
          ? "L'email serve solo a recuperare la password se la dimentichi. Puoi aggiungerla anche dopo."
          : 'Questo server non manda email: un indirizzo si può salvare, ma il recupero della password non è attivo.',
      }),
      button('Crea account', 'soft', () =>
        this.#run(() => this.#handlers.onRegister({
          userName: newName.input.value.trim(),
          password: newPassword.input.value,
          email: email.input.value.trim(),
        })),
      ),
    ]);

    const guest = panel('Oppure gioca e basta', [
      el('p', {
        className: 'stats-hint',
        text: 'Senza account si gioca lo stesso: al tavolo compari come ospite e le partite non finiscono nelle statistiche.',
      }),
      button('Gioca come ospite', 'soft', () => this.#handlers.onBack()),
    ]);

    return [signIn, register, guest];
  }

  /** Signed in **/

  #signedInPanels() {
    const account = this.#account ?? {};
    const panels = [
      panel('Sei dentro', [
        el('p', { className: 'account-name', text: account.userName ?? '' }),
        el('p', { className: 'stats-hint', text: this.#emailLine(account) }),
        button('Le tue statistiche', 'soft', () => this.#handlers.onStats()),
        button('Esci', 'soft', () => this.#run(() => this.#handlers.onLogout())),
      ]),
    ];

    if (account.claimableGames > 0) {
      panels.push(
        panel('Partite da collegare', [
          el('p', {
            className: 'stats-hint',
            text: `Su questo dispositivo ci sono ${account.claimableGames} ${
              account.claimableGames === 1 ? 'partita giocata' : 'partite giocate'
            } senza account. Vuoi metterle nella tua storia?`,
          }),
          button('Collegale al mio account', 'soft', () =>
            this.#run(() => this.#handlers.onClaimHistory()),
          ),
        ]),
      );
    }

    const email = field('profileEmail', 'Email', { type: 'email', autocomplete: 'email' });
    email.input.value = account.email ?? '';
    panels.push(
      panel('Email', [
        email.label,
        email.input,
        el('p', {
          className: 'stats-hint',
          text: 'Serve solo per recuperare la password. Lasciala vuota per toglierla.',
        }),
        button('Salva indirizzo', 'soft', () =>
          this.#run(() => this.#handlers.onSetEmail(email.input.value.trim())),
        ),
      ]),
    );

    const current = field('currentPassword', 'Password attuale', {
      type: 'password',
      autocomplete: 'current-password',
    });
    const fresh = field('freshPassword', 'Nuova password', {
      type: 'password',
      autocomplete: 'new-password',
    });
    panels.push(
      panel('Password', [
        current.label,
        current.input,
        fresh.label,
        fresh.input,
        el('p', {
          className: 'stats-hint',
          text: 'Cambiandola, gli altri dispositivi collegati a questo account vengono disconnessi.',
        }),
        button('Cambia password', 'soft', () =>
          this.#run(() => this.#handlers.onChangePassword({
            password: current.input.value,
            newPassword: fresh.input.value,
          })),
        ),
      ]),
    );

    const confirm = field('deletePassword', 'Password, per conferma', {
      type: 'password',
      autocomplete: 'current-password',
    });
    panels.push(
      panel('Cancella account', [
        el('p', {
          className: 'stats-hint',
          text: 'Il nome torna libero e la tua storia sparisce. Le partite restano nel conto degli altri giocatori, senza più il tuo nome sopra.',
        }),
        confirm.label,
        confirm.input,
        button('Cancella il mio account', 'soft', () => {
          if (!globalThis.confirm?.('Cancellare davvero il tuo account?')) return;
          this.#run(() => this.#handlers.onDeleteAccount(confirm.input.value));
        }),
      ]),
    );

    return panels;
  }

  #emailLine(account) {
    if (!account.email) return 'Nessuna email collegata: senza, una password persa è persa.';
    return account.emailVerified
      ? `${account.email} — confermata`
      : `${account.email} — da confermare: apri il link che ti abbiamo mandato`;
  }

  /** The two screens that belong to a link **/

  #forgotPanel() {
    const who = field('resetWho', 'Il tuo nome, oppure la tua email');
    return panel('Password dimenticata', [
      who.label,
      who.input,
      el('p', {
        className: 'stats-hint',
        text: 'Ti arriva un link che vale un\'ora. Funziona solo se avevi confermato un indirizzo.',
      }),
      button('Mandami il link', 'gold', () =>
        this.#run(() => this.#handlers.onRequestReset(who.input.value.trim())),
      ),
      link('Torna ad accedere', () => this.update({ mode: AccountMode.SIGNED_OUT })),
    ]);
  }

  #resetPanel() {
    const fresh = field('resetPassword', 'Nuova password (almeno 8 caratteri)', {
      type: 'password',
      autocomplete: 'new-password',
    });
    return panel('Scegli una nuova password', [
      el('p', {
        className: 'stats-hint',
        text: 'Hai aperto il link che ti abbiamo mandato. Dopo il cambio, tutti i dispositivi vanno rifatti accedere.',
      }),
      fresh.label,
      fresh.input,
      button('Salva la nuova password', 'gold', () =>
        this.#run(() => this.#handlers.onResetPassword({
          resetToken: this.#resetToken,
          newPassword: fresh.input.value,
        })),
      ),
    ]);
  }

  /** One command at a time, with its answer on screen **/

  async #run(command) {
    if (this.#busy) return;
    this.#busy = true;
    this.say('Un momento...');
    try {
      const message = await command();
      this.say(typeof message === 'string' && message ? message : '');
    } catch (error) {
      this.say(error.message, true);
    } finally {
      this.#busy = false;
    }
  }
}

function panel(title, children) {
  return el('div', { className: 'panel' }, [el('h2', { text: title }), children].flat());
}

// A label and its input, kept together so the two cannot drift apart
function field(id, text, attributes = {}) {
  return {
    label: el('label', { for: id, text }),
    input: el('input', { id, type: 'text', ...attributes }),
  };
}

function button(text, kind, onclick) {
  return el('button', { className: `button button-${kind}`, type: 'button', text, onclick });
}

function link(text, onclick) {
  return el('button', { className: 'link-button', type: 'button', text, onclick });
}
